import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Tag, Spin, Empty, Row, Col, Modal } from 'antd';
import { ArrowLeftOutlined, EditOutlined, EyeOutlined, DownloadOutlined, FileTextOutlined, SendOutlined, SyncOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, CloudUploadOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import { buscarHomologacao, obterUrlArquivoHomologacao, obterUrlDownloadHomologacao, uploadArquivoHomologacao, deletarArquivoHomologacao, buscarContratosPorHomologacao, gerarProcuracao, statusProcuracaoAutentique, enviarProcuracaoAutentique, syncProcuracaoAutentique, cancelarProcuracaoAutentique, uploadProcuracaoAssinada, type HomologacaoData, type HomologacaoArquivo, type ContratoResumido, type ProcuracaoAutentiqueStatus, type DadosManuaisProcuracao } from '../api/homologacao';
import { toast } from 'react-toastify';
import { useFormStore } from '../store/formStore';
import { buscarPedidosCalc, type CalcPedido } from '../api/crm';
import { extrairDadosPedido, filtrarPropostasValidas, type InversorProposta } from '../utils/proposta';

const STATUS_COLORS: Record<string, string> = {
  rascunho: 'default',
  finalizado: 'cyan',
  enviado: 'blue',
  aprovado: 'green',
  reprovado: 'red',
};

const CATEGORIA_LABELS: Record<string, string> = {
  fotos_padrao: 'Fotos do Padrão',
  imagens_diversas_padrao: 'Ramal/Disjuntor/Medidor/Caixa',
  foto_relacao_caixas: 'Relação de Caixas',
  foto_fachada: 'Fachada da Casa',
  foto_padrao_exemplo: 'Padrão (Exemplo)',
  print_localizacao: 'Print Localização Padrão',
  print_transformador: 'Print Transformador',
  foto_material_usina_existente: 'Material Usina Existente',
  doc_car: 'CAR (Cadastro Ambiental Rural)',
  doc_imovel: 'Documento do Imóvel',
  cessao_telhado: 'Cessão de Telhado',
  doc_identidade_titular: 'Documento de Identidade do Titular',
  iptu_certidao: 'IPTU ou Certidão de Número',
  print_marcacao_telhado: 'Print Marcação do Telhado',
  foto_telhado: 'Foto do Telhado',
  foto_estrutura_abaixo_telhado: 'Estrutura Abaixo do Telhado',
  foto_local_inversor: 'Local do Inversor',
  foto_acesso_telhado: 'Acesso ao Telhado',
  foto_quadro_conexao: 'Quadro de Conexão',
  foto_quadro_passagem: 'Quadro de Passagem',
  cartao_cnpj: 'Cartão CNPJ',
  contrato_social: 'Contrato Social',
  contrato_social_titular: 'Contrato Social do Titular',
  doc_identidade_cliente: 'Documento de Identidade do Cliente',
  comprovante_endereco_cliente: 'Comprovante de Endereço do Cliente',
  documentos_adicionais: 'Documentos Adicionais',
  doc_titular: 'Documento do Titular',
  procuracao_assinada: 'Procuração Assinada',
};


function ArquivoThumb({ arq, onDelete }: { arq: HomologacaoArquivo; onDelete?: (id: number) => void }) {
  const isImage = arq.mime_type?.startsWith('image/');
  const streamUrl = obterUrlArquivoHomologacao(arq.id);
  const downloadUrl = obterUrlDownloadHomologacao(arq.id);

  const handleView = () => { window.open(streamUrl, '_blank'); };

  const handleDownload = () => {
    window.open(downloadUrl, '_blank');
  };

  const isPdf = arq.mime_type === 'application/pdf';

  return (
    <div className="relative group border rounded-lg overflow-hidden bg-white cursor-pointer" style={{ width: 120, height: 120 }}>
      {isImage ? (
        <img src={streamUrl} alt={arq.nome_original} className="w-full h-full object-cover" />
      ) : isPdf ? (
        <div className="w-full h-full relative bg-gray-50">
          <iframe src={`${streamUrl}#toolbar=0&navpanes=0&scrollbar=0`} title={arq.nome_original}
            className="w-full h-full pointer-events-none" style={{ border: 'none' }} />
          <div className="absolute bottom-0 left-0 right-0 bg-white/80 px-1 py-0.5">
            <span className="text-[9px] text-gray-500 truncate block">{arq.nome_original}</span>
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 p-2">
          <span className="text-3xl mb-1">arquivo</span>
          <span className="text-[10px] text-gray-500 truncate w-full text-center">{arq.nome_original}</span>
        </div>
      )}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
        <button type="button" onClick={handleView} className="text-white hover:text-blue-300" title="Visualizar"><EyeOutlined style={{ fontSize: 18 }} /></button>
        <button type="button" onClick={handleDownload} className="text-white hover:text-green-300" title="Baixar"><DownloadOutlined style={{ fontSize: 18 }} /></button>
        {onDelete && <button type="button" onClick={() => onDelete(arq.id)} className="text-white hover:text-red-300" title="Remover"><DeleteOutlined style={{ fontSize: 18 }} /></button>}
      </div>
    </div>
  );
}

const ESTRUTURA_LABELS: Record<string, string> = {
  Metalico: 'Metálico', Ceramico: 'Cerâmico', Solo: 'Solo', Carport: 'Carport',
  Fibrocimento: 'Fibrocimento', Fibromadeira: 'Fibromadeira', Laje: 'Laje',
};

const FASES_LABELS: Record<string, string> = { Monofasico: 'Monofásico', Bifasico: 'Bifásico', Trifasico: 'Trifásico' };

const formatarBRL = (v: unknown) => {
  const n = Number(v);
  return v === '' || v == null || !Number.isFinite(n) || n === 0
    ? '' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const capitalizar = (v: unknown) => {
  const t = String(v ?? '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

// Inversores da homologação: lista completa quando gravada; senão os campos 1..3 (padrão do contrato).
function listarInversores(d: Record<string, any>): InversorProposta[] {
  if (Array.isArray(d?.inversores) && d.inversores.length) return d.inversores;
  const out: InversorProposta[] = [];
  for (const i of [1, 2, 3]) {
    const modelo = d?.[`modeloInversor${i}`];
    if (modelo) out.push({ modelo, quantidade: Number(d?.[`qtdInversor${i}`]) || 0 });
  }
  return out;
}

function InfoLink({ label, href, text }: { label: string; href?: string; text?: string }) {
  return (
    <div className="mb-2">
      <span className="text-xs text-gray-500 block">{label}</span>
      {href
        ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 underline break-all">{text || href}</a>
        : <span className="text-sm text-gray-300">-</span>}
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: unknown }) {
  const display = value === undefined || value === null || value === '' ? '-' : String(value);
  const isEmpty = display === '-';
  return (
    <div className="mb-2">
      <span className="text-xs text-gray-500 block">{label}</span>
      <span className={`text-sm ${isEmpty ? 'text-gray-300' : 'text-gray-800'}`}>{display}</span>
    </div>
  );
}

const UF_OPTIONS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const ESTADO_CIVIL_OPTIONS = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'];

function FormularioManualProcuracao({ dados, onChange, signerForm, onSignerChange, onGerar, onEnviar, onAnexarAssinada, onVoltar, gerando, loading, anexando, somentePreenchimento }: {
  dados: DadosManuaisProcuracao;
  onChange: (d: DadosManuaisProcuracao) => void;
  signerForm: { name: string; email: string; phone: string; deliveryMethod: string; message: string };
  onSignerChange: (s: typeof signerForm) => void;
  onGerar: () => void;
  onEnviar: () => void;
  onAnexarAssinada: () => void;
  onVoltar?: () => void;
  gerando: boolean;
  loading: boolean;
  anexando: boolean;
  somentePreenchimento?: boolean;
}) {
  const set = (k: keyof DadosManuaisProcuracao) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ ...dados, [k]: e.target.value });

  const field = (label: string, k: keyof DadosManuaisProcuracao, props?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input value={dados[k] as string || ''} onChange={set(k)} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" {...props} />
    </div>
  );

  return (
    <div>
      {onVoltar && (
        <button onClick={onVoltar} className="text-xs text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1">
          ← Voltar para contratos
        </button>
      )}
      <p className="text-xs text-gray-400 mb-4">Confira e ajuste os dados antes de gerar a procuração.</p>

      <div className="mb-2">
        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Cliente</label>
        <select value={dados.tipoCliente || 'PF'} onChange={set('tipoCliente') as (e: React.ChangeEvent<HTMLSelectElement>) => void} className="w-full sm:w-1/2 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
          <option value="PF">Pessoa Física (PF)</option>
          <option value="PJ">Pessoa Jurídica (PJ)</option>
        </select>
      </div>

      {(dados.tipoCliente === 'PJ' || (dados.tipoCliente || '').toLowerCase().includes('jur')) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-3">
          <p className="text-xs font-medium text-gray-600 mb-2">Dados da Empresa</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            {field('Razão Social *', 'razaoSocial')}
            {field('CNPJ *', 'cnpjEmpresa', { placeholder: '00.000.000/0000-00' })}
            {field('Tipo PJ (Ltda, S.A., MEI...)', 'tipoPessoaJuridica')}
            {field('Finalidade da Empresa', 'finalidadeEmpresa')}
          </div>
        </div>
      )}

      <p className="text-xs font-medium text-gray-600 mb-2">{(dados.tipoCliente === 'PJ' || (dados.tipoCliente || '').toLowerCase().includes('jur')) ? 'Responsável Legal' : 'Titular'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        {field('Nome completo *', 'nomeResponsavel')}
        {field('CPF', 'cpfResponsavel', { placeholder: '000.000.000-00' })}
        {(dados.tipoCliente === 'PJ' || (dados.tipoCliente || '').toLowerCase().includes('jur')) && field('Cargo', 'cargoResponsavel')}
        {field('E-mail', 'email', { type: 'email', placeholder: 'email@exemplo.com' })}
        {field('Telefone', 'telefone', { placeholder: '(00) 00000-0000' })}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Estado Civil</label>
          <select value={dados.estadoCivil || ''} onChange={set('estadoCivil') as (e: React.ChangeEvent<HTMLSelectElement>) => void} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
            <option value="">Selecione</option>
            {ESTADO_CIVIL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        {field('Profissão', 'profissao')}
      </div>

      <p className="text-xs font-medium text-gray-600 mb-2 mt-2">Endereço {(dados.tipoCliente === 'PJ' || (dados.tipoCliente || '').toLowerCase().includes('jur')) ? 'da Empresa' : 'de Instalação'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        {field('Logradouro', 'logradouroInstalacao')}
        {field('Número', 'numeroInstalacao')}
        {field('Complemento', 'complementoInstalacao')}
        {field('Bairro', 'bairroInstalacao')}
        {field('Cidade', 'cidadeInstalacao')}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">UF</label>
          <select value={dados.ufInstalacao || ''} onChange={set('ufInstalacao') as (e: React.ChangeEvent<HTMLSelectElement>) => void} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
            <option value="">Selecione</option>
            {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
          </select>
        </div>
        {field('CEP', 'cepInstalacao', { placeholder: '00000-000' })}
      </div>

      {!somentePreenchimento && (
      <div className="border-t border-gray-100 mt-2 pt-4">
        <p className="text-xs font-medium text-gray-500 mb-3">Envio para assinatura</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">Nome do signatário *</label>
            <input value={signerForm.name} onChange={e => onSignerChange({ ...signerForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">Método de envio</label>
            <select value={signerForm.deliveryMethod} onChange={e => onSignerChange({ ...signerForm, deliveryMethod: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
              <option value="email">E-mail</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          {signerForm.deliveryMethod === 'email' ? (
            <div className="mb-3 sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">E-mail *</label>
              <input value={signerForm.email} onChange={e => onSignerChange({ ...signerForm, email: e.target.value })} type="email" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
            </div>
          ) : (
            <div className="mb-3 sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">WhatsApp *</label>
              <input value={signerForm.phone} onChange={e => onSignerChange({ ...signerForm, phone: e.target.value })} placeholder="(00) 00000-0000" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
            </div>
          )}
        </div>
      </div>

      )}

      <div className="flex flex-wrap gap-2 mt-2">
        <Button icon={<DownloadOutlined />} loading={gerando} onClick={onGerar}>Baixar PDF</Button>
        {!somentePreenchimento && (<>
        <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={onEnviar} disabled={!dados.nomeResponsavel || !signerForm.name}>
          Enviar para Assinatura
        </Button>
        <Button icon={<CloudUploadOutlined />} loading={anexando} onClick={onAnexarAssinada} disabled={!dados.nomeResponsavel || !signerForm.name}>
          Já tenho assinada — anexar
        </Button>
        </>)}
      </div>
    </div>
  );
}

export default function HomologacaoDetalhesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<HomologacaoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCategoria, setUploadCategoria] = useState('');

  const reloadData = useCallback(async () => {
    if (!id) return;
    const fresh = await buscarHomologacao(Number(id));
    setData(fresh);
  }, [id]);

  const handleUpload = useCallback(async (categoria: string, file: File) => {
    if (!data) return;
    setUploading(true);
    try {
      await uploadArquivoHomologacao(data.id, categoria, file);
      await reloadData();
      toast.success(`Arquivo enviado: ${file.name}`);
    } catch { toast.error('Erro ao enviar arquivo'); }
    finally { setUploading(false); setUploadCategoria(''); }
  }, [data, reloadData]);

  const handleDelete = useCallback(async (arquivoId: number) => {
    if (!window.confirm('Remover este arquivo?')) return;
    try {
      await deletarArquivoHomologacao(arquivoId);
      await reloadData();
      toast.success('Arquivo removido');
    } catch { toast.error('Erro ao remover arquivo'); }
  }, [reloadData]);

  // Procuração state
  const [procModalOpen, setProcModalOpen] = useState(false);
  const [procTab, setProcTab] = useState<'contratos' | 'autentique'>('contratos');
  const [procBuscando, setProcBuscando] = useState(false);
  const [procGerando, setProcGerando] = useState<string | null>(null);
  const [contratos, setContratos] = useState<ContratoResumido[]>([]);
  const [clienteInfo, setClienteInfo] = useState<{ nome: string | null; cpf: string | null }>({ nome: null, cpf: null });

  // Autentique state
  const [autStatus, setAutStatus] = useState<ProcuracaoAutentiqueStatus | null>(null);
  const [autSyncData, setAutSyncData] = useState<{ status: string; signers: { name: string; email: string; status: string; signedAt?: string; rejectedAt?: string; rejectedReason?: string; link?: string | null }[]; signedUrl?: string } | null>(null);
  const [autLoading, setAutLoading] = useState(false);
  const [autSyncing, setAutSyncing] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [selectedContratoId, setSelectedContratoId] = useState<string | null>(null);
  const [modoManual, setModoManual] = useState(false);
  const [dadosManuais, setDadosManuais] = useState<DadosManuaisProcuracao>({ nomeResponsavel: '', cpfResponsavel: '', estadoCivil: '', profissao: '', tipoCliente: 'PF', logradouroInstalacao: '', numeroInstalacao: '', complementoInstalacao: '', bairroInstalacao: '', cidadeInstalacao: '', ufInstalacao: '', cepInstalacao: '' });
  const [signerForm, setSignerForm] = useState({ name: '', email: '', phone: '', deliveryMethod: 'email', message: 'Prezado(a), segue a procuração para assinatura digital.' });

  // Upload de procuração já assinada
  const [anexandoAssinada, setAnexandoAssinada] = useState(false);
  const anexarAssinadaInputRef = React.useRef<HTMLInputElement | null>(null);
  const anexarAssinadaCtxRef = React.useRef<{ contratoId?: string; manual: boolean; signerNameOverride?: string } | null>(null);

  const loadAutentiqueStatus = useCallback(async () => {
    if (!data) return;
    try {
      const s = await statusProcuracaoAutentique(data.id);
      setAutStatus(s);
      if (s.enviada) {
        setProcTab('autentique');
        // Sync details
        setAutSyncing(true);
        try {
          const sync = await syncProcuracaoAutentique(data.id);
          if (sync.success) setAutSyncData(sync);
        } catch { /* ignore */ }
        setAutSyncing(false);
      }
    } catch { /* ignore */ }
  }, [data]);

  const handleAbrirProcuracao = useCallback(async () => {
    if (!data) return;
    setProcModalOpen(true);
    setProcBuscando(true);
    setContratos([]);
    setModoManual(false);
    try {
      const homDados = typeof data.dados === 'string' ? JSON.parse(data.dados) : data.dados;
      const [result] = await Promise.all([
        buscarContratosPorHomologacao(data.id),
        loadAutentiqueStatus(),
      ]);
      setContratos(result.contratos);
      setClienteInfo({ nome: result.clienteNome, cpf: result.clienteCpf });

      // Determina origem dos dados: titular (se diferente do cliente) ou cliente
      const titularDiferente = homDados?.titularIgualCliente === false;
      const titularPJ = titularDiferente && homDados?.titularTipo === 'Pessoa Jurídica';
      const clientePJ = !titularDiferente && homDados?.tipoCliente === 'Pessoa Jurídica';
      const ehPJ = titularDiferente ? titularPJ : clientePJ;

      // Nome e CPF do responsável legal (sempre o nome de uma pessoa, mesmo em PJ)
      const nomeResp = titularDiferente
        ? (homDados?.titularNome || '')
        : (homDados?.nomeResponsavel || '');
      const cpfResp = titularDiferente
        ? (homDados?.titularCpf || '')
        : (homDados?.cpfResponsavel || '');
      const emailResp = titularDiferente
        ? (homDados?.titularEmail || '')
        : (homDados?.email || '');
      const telResp = titularDiferente
        ? (homDados?.titularTelefone || '')
        : (homDados?.telefone || '');

      // Dados PJ
      const razaoSoc = titularDiferente
        ? (titularPJ ? (homDados?.titularRazaoSocial || '') : '')
        : (homDados?.razaoSocial || '');
      const cnpj = titularDiferente
        ? (titularPJ ? (homDados?.titularCnpj || '') : '')
        : (homDados?.cnpjEmpresa || '');
      const cargo = titularDiferente
        ? (titularPJ ? (homDados?.titularCargoResponsavel || '') : '')
        : (homDados?.cargoResponsavel || '');
      const finalidade = titularDiferente
        ? (titularPJ ? (homDados?.titularFinalidadeEmpresa || '') : '')
        : (homDados?.finalidadeEmpresa || '');

      // Endereço — usa instalação se diferente do faturamento, senão usa faturamento
      const usaFat = homDados?.instalacaoIgualContratual === true || homDados?.instalacaoIgualContratual === 'Sim';
      const enderecoPrefix = usaFat ? 'Faturamento' : 'Instalacao';
      const logr = homDados?.[`logradouro${enderecoPrefix}`] || '';
      const num = homDados?.[`numero${enderecoPrefix}`] || '';
      const compl = homDados?.[`complemento${enderecoPrefix}`] || '';
      const bairro = homDados?.[`bairro${enderecoPrefix}`] || '';
      const cidade = homDados?.[`cidade${enderecoPrefix}`] || '';
      const uf = homDados?.[`uf${enderecoPrefix}`] || '';
      const cep = homDados?.[`cep${enderecoPrefix}`] || '';

      // Sempre pré-preenche os dadosManuais com tudo da homologação — fica disponível
      // tanto quando o usuário entra em modo manual quanto se quiser editar
      setDadosManuais({
        tipoCliente: ehPJ ? 'PJ' : 'PF',
        nomeResponsavel: nomeResp,
        cpfResponsavel: cpfResp,
        email: emailResp,
        telefone: telResp,
        estadoCivil: titularDiferente ? (homDados?.titularEstadoCivil || '') : (homDados?.estadoCivil || ''),
        profissao: titularDiferente ? (homDados?.titularProfissao || '') : (homDados?.profissao || ''),
        razaoSocial: razaoSoc,
        cnpjEmpresa: cnpj,
        cargoResponsavel: cargo,
        finalidadeEmpresa: finalidade,
        tipoPessoaJuridica: '',
        logradouroInstalacao: logr,
        numeroInstalacao: num,
        complementoInstalacao: compl,
        bairroInstalacao: bairro,
        cidadeInstalacao: cidade,
        ufInstalacao: uf,
        cepInstalacao: cep,
      });

      if (result.contratos.length > 0) {
        const c = result.contratos[0];
        setSelectedContratoId(c.id);
        setSignerForm(prev => ({
          ...prev,
          name: nomeResp || c.nomeResponsavel || '',
          email: emailResp || prev.email,
          phone: telResp || prev.phone,
        }));
      } else {
        setModoManual(true);
        setSignerForm(prev => ({
          ...prev,
          name: nomeResp || '',
          email: emailResp || prev.email,
          phone: telResp || prev.phone,
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro ao buscar contratos do cliente');
    } finally {
      setProcBuscando(false);
    }
  }, [data, loadAutentiqueStatus]);

  const [baixandoDireto, setBaixandoDireto] = useState(false);
  const handleBaixarProcuracaoDireta = async () => {
    if (!data) return;
    const homDados = typeof data.dados === 'string' ? JSON.parse(data.dados) : data.dados;
    const titularDiferente = homDados?.titularIgualCliente === false;
    const titularPJ = titularDiferente && homDados?.titularTipo === 'Pessoa Jurídica';
    const clientePJ = !titularDiferente && homDados?.tipoCliente === 'Pessoa Jurídica';
    const ehPJ = titularDiferente ? titularPJ : clientePJ;
    const usaFat = homDados?.instalacaoIgualContratual === true || homDados?.instalacaoIgualContratual === 'Sim';
    const pref = usaFat ? 'Faturamento' : 'Instalacao';

    const dm = {
      tipoCliente: ehPJ ? 'PJ' : 'PF',
      nomeResponsavel: titularDiferente ? (homDados?.titularNome || '') : (homDados?.nomeResponsavel || ''),
      cpfResponsavel: titularDiferente ? (homDados?.titularCpf || '') : (homDados?.cpfResponsavel || ''),
      estadoCivil: titularDiferente ? (homDados?.titularEstadoCivil || '') : (homDados?.estadoCivil || ''),
      profissao: titularDiferente ? (homDados?.titularProfissao || '') : (homDados?.profissao || ''),
      email: titularDiferente ? (homDados?.titularEmail || '') : (homDados?.email || ''),
      telefone: titularDiferente ? (homDados?.titularTelefone || '') : (homDados?.telefone || ''),
      razaoSocial: titularDiferente ? (homDados?.titularRazaoSocial || '') : (homDados?.razaoSocial || ''),
      cnpjEmpresa: titularDiferente ? (homDados?.titularCnpj || '') : (homDados?.cnpjEmpresa || ''),
      cargoResponsavel: titularDiferente ? (homDados?.titularCargoResponsavel || '') : (homDados?.cargoResponsavel || ''),
      finalidadeEmpresa: titularDiferente ? (homDados?.titularFinalidadeEmpresa || '') : (homDados?.finalidadeEmpresa || ''),
      logradouroInstalacao: homDados?.[`logradouro${pref}`] || '',
      numeroInstalacao: homDados?.[`numero${pref}`] || '',
      complementoInstalacao: homDados?.[`complemento${pref}`] || '',
      bairroInstalacao: homDados?.[`bairro${pref}`] || '',
      cidadeInstalacao: homDados?.[`cidade${pref}`] || '',
      ufInstalacao: homDados?.[`uf${pref}`] || '',
      cepInstalacao: homDados?.[`cep${pref}`] || '',
    };

    setBaixandoDireto(true);
    try {
      const blob = await gerarProcuracao({ dadosManuais: dm, homologacaoId: data.id });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const nomeArq = (ehPJ ? dm.razaoSocial : dm.nomeResponsavel) || 'cliente';
      a.download = `Procuracao_${ehPJ ? 'PJ' : 'PF'}_${nomeArq.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Procuração baixada');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar procuração');
    } finally {
      setBaixandoDireto(false);
    }
  };

  const handleGerarProcuracao = async (contratoId?: string) => {
    if (!data) return;
    setProcGerando(contratoId || 'manual');
    try {
      const args = modoManual
        ? { dadosManuais, homologacaoId: data.id }
        : { contratoId: contratoId!, homologacaoId: data.id };
      const blob = await gerarProcuracao(args);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Procuracao_PF_${dadosManuais.nomeResponsavel || contratoId || 'cliente'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Procuração gerada com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar procuração');
    } finally {
      setProcGerando(null);
    }
  };

  const handleEnviarAutentique = async () => {
    if (!data || (!selectedContratoId && !modoManual) || !signerForm.name) return;
    if (modoManual && !dadosManuais.nomeResponsavel) {
      toast.error('Nome do titular é obrigatório');
      return;
    }
    if (signerForm.deliveryMethod === 'email' && !signerForm.email) {
      toast.error('E-mail do signatário é obrigatório');
      return;
    }
    if ((signerForm.deliveryMethod === 'whatsapp' || signerForm.deliveryMethod === 'sms') && !signerForm.phone) {
      toast.error('Telefone do signatário é obrigatório');
      return;
    }

    setAutLoading(true);
    try {
      await enviarProcuracaoAutentique({
        contratoId: modoManual ? undefined : selectedContratoId!,
        dadosManuais: modoManual ? dadosManuais : undefined,
        homologacaoId: data.id,
        signerName: signerForm.name,
        signerEmail: signerForm.email || undefined,
        signerPhone: signerForm.phone || undefined,
        deliveryMethod: signerForm.deliveryMethod,
        message: signerForm.message,
        forcar: reenviando || undefined,
      });
      toast.success(reenviando ? 'Procuração reenviada!' : 'Procuração enviada para assinatura!');
      setReenviando(false);
      setAutSyncData(null);
      await loadAutentiqueStatus();
    } catch (err) {
      console.error(err);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Erro ao enviar para Autentique');
    } finally {
      setAutLoading(false);
    }
  };

  const handleSyncAutentique = async () => {
    if (!data) return;
    setAutSyncing(true);
    try {
      const sync = await syncProcuracaoAutentique(data.id);
      if (sync.success) {
        setAutSyncData(sync);
        setAutStatus(prev => prev ? { ...prev, status: sync.status } : prev);
        toast.success('Status atualizado');
      }
    } catch { toast.error('Erro ao sincronizar'); }
    finally { setAutSyncing(false); }
  };

  const triggerAnexarAssinada = (ctx: { contratoId?: string; manual: boolean; signerNameOverride?: string }) => {
    if (!data) return;
    if (ctx.manual && !dadosManuais.nomeResponsavel) {
      toast.error('Preencha o nome do titular antes de anexar');
      return;
    }
    const effectiveName = ctx.signerNameOverride || signerForm.name;
    if (!effectiveName) {
      toast.error('Informe o nome do signatário antes de anexar');
      return;
    }
    anexarAssinadaCtxRef.current = ctx;
    if (anexarAssinadaInputRef.current) {
      anexarAssinadaInputRef.current.value = '';
      anexarAssinadaInputRef.current.click();
    }
  };

  const handleAnexarAssinadaFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !data) return;
    const ctx = anexarAssinadaCtxRef.current;
    if (!ctx) return;
    setAnexandoAssinada(true);
    try {
      await uploadProcuracaoAssinada({
        file,
        homologacaoId: data.id,
        signerName: ctx.signerNameOverride || signerForm.name,
        signerEmail: signerForm.email || undefined,
        signerPhone: signerForm.phone || undefined,
        contratoId: ctx.manual ? undefined : ctx.contratoId,
        dadosManuais: ctx.manual ? dadosManuais : undefined,
        forcar: reenviando || undefined,
      });
      toast.success('Procuração assinada anexada e enviada!');
      setReenviando(false);
      setAutSyncData(null);
      await Promise.all([loadAutentiqueStatus(), reloadData()]);
      setProcTab('autentique');
    } catch (err: unknown) {
      console.error(err);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Erro ao anexar procuração assinada');
    } finally {
      setAnexandoAssinada(false);
      anexarAssinadaCtxRef.current = null;
    }
  };

  const handleCancelarAutentique = async () => {
    if (!data || !window.confirm('Tem certeza que deseja cancelar a procuração no Autentique?')) return;
    setAutLoading(true);
    try {
      await cancelarProcuracaoAutentique(data.id);
      setAutStatus({ enviada: false });
      setAutSyncData(null);
      setProcTab('contratos');
      toast.success('Procuração cancelada');
    } catch { toast.error('Erro ao cancelar'); }
    finally { setAutLoading(false); }
  };

  const loadCatalogos = useFormStore((s) => s.loadCatalogos);
  const concessionarias = useFormStore((s) => s.concessionarias);
  useEffect(() => { loadCatalogos(); }, [loadCatalogos]);

  // Proposta mais recente do cliente no calc, pra avisar quando a homologação ficou
  // vinculada a uma proposta anterior.
  const [propostaMaisRecente, setPropostaMaisRecente] = useState<CalcPedido | null>(null);
  useEffect(() => {
    if (!data?.cliente_id) return;
    buscarPedidosCalc(String(data.cliente_id))
      .then((pedidos) => {
        const validas = filtrarPropostasValidas(pedidos);
        const base = validas.length ? validas : pedidos;
        const ordenadas = [...base].sort((a: any, b: any) => String(b.Data || '').localeCompare(String(a.Data || '')));
        setPropostaMaisRecente(ordenadas[0] || null);
      })
      .catch(() => setPropostaMaisRecente(null));
  }, [data?.cliente_id]);

  useEffect(() => {
    if (!id) return;
    buscarHomologacao(Number(id))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center p-20"><Spin size="large" /></div>;
  if (!data) return <div className="max-w-5xl mx-auto p-4"><Empty description="Homologação não encontrada" /></div>;

  const d = typeof data.dados === 'string' ? JSON.parse(data.dados) : data.dados;
  const arquivos = data.arquivos || [];

  const hasAny = (keys: string[]): boolean =>
    keys.some((k) => {
      const v = d?.[k];
      return v !== undefined && v !== null && v !== '' && v !== 0;
    });

  const hasCliente = hasAny(['tipoCliente', 'nomeResponsavel', 'cpfResponsavel', 'email', 'telefone', 'estadoCivil', 'profissao', 'razaoSocial', 'cnpjEmpresa', 'finalidadeEmpresa', 'cargoResponsavel']);
  const hasTitular = d?.titularIgualCliente === false || hasAny(['titularNome', 'titularCpf', 'titularCnpj', 'titularEmail', 'titularTelefone', 'titularRazaoSocial']);
  const hasEndereco = hasAny(['cepFaturamento', 'logradouroFaturamento', 'numeroFaturamento', 'bairroFaturamento', 'cidadeFaturamento', 'ufFaturamento', 'cepInstalacao', 'logradouroInstalacao', 'numeroInstalacao', 'bairroInstalacao', 'cidadeInstalacao', 'ufInstalacao']);
  const hasContratoProjeto = hasAny(['modalidadeCompensacao', 'localInstalacao', 'tipoEstrutura', 'valorProposta', 'modeloModulo', 'marcaInversores', 'modeloInversor1', 'qtdInversores', 'qtdInversor1', 'potenciaTotalInversores']) || d?.contratoComSeguro === true;
  // quantidadePadroes vem sempre 1 por default — sozinho não indica dado preenchido.
  const hasContratoServicos = hasAny(['situacaoPadrao', 'tipoConexaoPadrao', 'correnteDisjuntor', 'favorOuContraRede', 'servicoAlvenariaWiz', 'valorPadrao', 'aditivos']);

  const concessionariaNome = (() => {
    const c = concessionarias.find((x) => String(x.id) === String(d?.concessionaria));
    return c?.nome || d?.concessionaria;
  })();
  const estruturaInstalacao = ESTRUTURA_LABELS[d?.tipo_estrutura_instalacao] || d?.tipo_estrutura_instalacao;
  const inversoresLista = listarInversores(d);
  const hasMateriais = !!(d?.modeloModulo || d?.numeroProposta || inversoresLista.length || Number(d?.potenciaTotalInversores) > 0);
  const hasLocalizacao = hasAny(['coordenada_geografica', 'url_local_instalacao', 'link_street_view', 'coordenadas_transformador', 'tipo_estrutura_instalacao']);
  const mapsUrl = d?.url_local_instalacao
    || (d?.coordenada_geografica ? `https://www.google.com/maps?q=${encodeURIComponent(String(d.coordenada_geografica).replace(/\s+/g, ''))}` : '');
  const propostaDesatualizada = !!(propostaMaisRecente && d?.idProposta && propostaMaisRecente.IdPedidoBanco !== d.idProposta);
  const dadosPropostaRecente = propostaDesatualizada && propostaMaisRecente ? extrairDadosPedido(propostaMaisRecente) : null;
  // Sem materiais gravados (ex.: a proposta já tinha vencido quando a homologação foi preenchida):
  // mostra os da proposta mais recente do Calc, sinalizando que não está vinculada.
  const materiaisCalc = !hasMateriais && propostaMaisRecente ? extrairDadosPedido(propostaMaisRecente) : null;

  // Group arquivos by categoria
  const grouped: Record<string, HomologacaoArquivo[]> = {};
  for (const arq of arquivos) {
    if (!grouped[arq.categoria]) grouped[arq.categoria] = [];
    grouped[arq.categoria].push(arq);
  }

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4">
            <Button icon={<ArrowLeftOutlined />} onClick={() => data.cliente_id ? navigate(`/homologacoes/${data.cliente_id}`) : navigate(-1)} />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-blue-800">Homologação #{data.id}</h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                Criado: {new Date(data.created_at).toLocaleDateString('pt-BR')} | Atualizado: {new Date(data.updated_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 ml-10 sm:ml-0 sm:justify-end sm:shrink-0">
            {d.homologacao_previa === true && (
              <Tag color="purple" className="text-xs sm:text-sm px-2 py-0.5">Prévia</Tag>
            )}
            <Tag color={STATUS_COLORS[data.status] || 'default'} className="text-sm sm:text-base px-2 sm:px-3 py-0.5 sm:py-1">{data.status}</Tag>
            <Button icon={<HistoryOutlined />} size="small" onClick={() => navigate(`/homologacao/historico/${data.id}`)}>Histórico</Button>
            <Button icon={<DownloadOutlined />} size="small" loading={baixandoDireto} onClick={handleBaixarProcuracaoDireta}>Baixar Procuração</Button>
            {['rascunho', 'finalizado', 'enviado', 'reprovado'].includes(data.status) && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  if (data.status !== 'rascunho') {
                    const temProcuracao = !!(d?.procuracao_autentique_doc_id);
                    const aviso = temProcuracao
                      ? 'Esta homologação já foi finalizada e possui procuração enviada. Ao editá-la, a procuração atual será invalidada e precisará ser reenviada. Deseja continuar?'
                      : 'Esta homologação já foi finalizada. Deseja editá-la?';
                    if (!window.confirm(aviso)) return;
                  }
                  navigate(`/homologacao/${data.id}`);
                }}
              >
                Editar
              </Button>
            )}
          </div>
        </div>
        {/* Botão Enviar Procuração — dentro do header pra garantir visibilidade em iframe */}
        <div className="mt-3 pt-3 border-t border-blue-100">
          <Button type="primary" icon={<SendOutlined />} size="large" onClick={handleAbrirProcuracao} block>
            Enviar Procuração
          </Button>
        </div>
      </div>

      {/* Homologação Prévia — Cliente / Titular / Endereço */}
      {hasCliente && (
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">
              Dados do Cliente
              {d.homologacao_previa === true && <Tag color="blue" className="ml-2 text-[10px]">Homologação Prévia</Tag>}
            </h2>
            <Row gutter={16}>
              <Col xs={24} sm={8}><InfoItem label="Tipo de Cliente" value={d.tipoCliente} /></Col>
              <Col xs={24} sm={8}><InfoItem label={d.tipoCliente === 'Pessoa Jurídica' ? 'Nome do Responsável' : 'Nome Completo'} value={d.nomeResponsavel} /></Col>
              <Col xs={24} sm={8}><InfoItem label="CPF do Responsável" value={d.cpfResponsavel} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}><InfoItem label="E-mail" value={d.email} /></Col>
              <Col xs={24} sm={12}><InfoItem label="Telefone" value={d.telefone} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}><InfoItem label="Estado Civil" value={d.estadoCivil} /></Col>
              <Col xs={24} sm={12}><InfoItem label="Profissão" value={d.profissao} /></Col>
            </Row>
            {d.tipoCliente === 'Pessoa Jurídica' && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><InfoItem label="Razão Social" value={d.razaoSocial} /></Col>
                  <Col xs={24} sm={12}><InfoItem label="CNPJ" value={d.cnpjEmpresa} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><InfoItem label="Finalidade da Empresa" value={d.finalidadeEmpresa} /></Col>
                  <Col xs={24} sm={12}><InfoItem label="Cargo do Responsável" value={d.cargoResponsavel} /></Col>
                </Row>
              </>
            )}
        </div>
      )}

      {hasTitular && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">Titular da Conta de Energia</h2>
            <Row gutter={16}>
              <Col xs={24}><InfoItem label="Titular é o mesmo cliente?" value={d.titularIgualCliente === false ? 'Não' : 'Sim'} /></Col>
            </Row>
            {d.titularIgualCliente === false && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><InfoItem label="Tipo do Titular" value={d.titularTipo} /></Col>
                  <Col xs={24} sm={8}><InfoItem label="Nome do Titular" value={d.titularNome} /></Col>
                  <Col xs={24} sm={8}><InfoItem label={d.titularTipo === 'Pessoa Jurídica' ? 'CNPJ do Titular' : 'CPF do Titular'} value={d.titularTipo === 'Pessoa Jurídica' ? d.titularCnpj : d.titularCpf} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><InfoItem label="E-mail do Titular" value={d.titularEmail} /></Col>
                  <Col xs={24} sm={12}><InfoItem label="Telefone do Titular" value={d.titularTelefone} /></Col>
                </Row>
                {d.titularTipo === 'Pessoa Jurídica' ? (
                  <Row gutter={16}>
                    <Col xs={24} sm={12}><InfoItem label="Razão Social" value={d.titularRazaoSocial} /></Col>
                    <Col xs={24} sm={12}><InfoItem label="Cargo do Responsável" value={d.titularCargoResponsavel} /></Col>
                    <Col xs={24}><InfoItem label="Finalidade da Empresa" value={d.titularFinalidadeEmpresa} /></Col>
                  </Row>
                ) : (
                  <Row gutter={16}>
                    <Col xs={24} sm={12}><InfoItem label="Estado Civil" value={d.titularEstadoCivil} /></Col>
                    <Col xs={24} sm={12}><InfoItem label="Profissão" value={d.titularProfissao} /></Col>
                  </Row>
                )}
              </>
            )}
          </div>
      )}

      {hasEndereco && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">Endereço de Faturamento</h2>
            <Row gutter={16}>
              <Col xs={24} sm={8}><InfoItem label="CEP" value={d.cepFaturamento} /></Col>
              <Col xs={24} sm={16}><InfoItem label="Logradouro" value={d.logradouroFaturamento} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={6}><InfoItem label="Número" value={d.numeroFaturamento} /></Col>
              <Col xs={24} sm={8}><InfoItem label="Complemento" value={d.complementoFaturamento} /></Col>
              <Col xs={24} sm={10}><InfoItem label="Bairro" value={d.bairroFaturamento} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={16}><InfoItem label="Cidade" value={d.cidadeFaturamento} /></Col>
              <Col xs={24} sm={8}><InfoItem label="UF" value={d.ufFaturamento} /></Col>
            </Row>

            <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-3 pt-4 border-t border-gray-100">Endereço de Instalação</h3>
            <Row gutter={16}>
              <Col xs={24}><InfoItem label="Instalação igual ao faturamento?" value={d.instalacaoIgualContratual === false ? 'Não' : 'Sim'} /></Col>
            </Row>
            {d.instalacaoIgualContratual === false && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><InfoItem label="CEP" value={d.cepInstalacao} /></Col>
                  <Col xs={24} sm={16}><InfoItem label="Logradouro" value={d.logradouroInstalacao} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={6}><InfoItem label="Número" value={d.numeroInstalacao} /></Col>
                  <Col xs={24} sm={8}><InfoItem label="Complemento" value={d.complementoInstalacao} /></Col>
                  <Col xs={24} sm={10}><InfoItem label="Bairro" value={d.bairroInstalacao} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={16}><InfoItem label="Cidade" value={d.cidadeInstalacao} /></Col>
                  <Col xs={24} sm={8}><InfoItem label="UF" value={d.ufInstalacao} /></Col>
                </Row>
              </>
            )}
          </div>
      )}

      {/* Projeto */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">Informações do Projeto</h2>
        <Row gutter={16}>
          <Col xs={12} sm={6}><InfoItem label="Potência Adquirida (kWp)" value={d.potencia_adquirida_kwp} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Número de Módulos" value={d.numero_modulos} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Concessionária" value={concessionariaNome} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Local de Instalação" value={capitalizar(d.tipo_localizacao)} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Tipo de Estrutura" value={estruturaInstalacao} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Ampliação de Usina" value={d.ampliacao_usina ? 'Sim' : 'Não'} /></Col>
        </Row>
        {d.ampliacao_usina && (
          <Row gutter={16}>
            <Col xs={12} sm={8}><InfoItem label="Potência Existente (kW)" value={d.potencia_existente_kw} /></Col>
            <Col xs={24} sm={16}><InfoItem label="Descrição Usina Existente" value={d.descricao_usina_existente} /></Col>
          </Row>
        )}
        {(hasLocalizacao || d.tipo_localizacao === 'rural') && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 rounded-full bg-black" />
              <span className="text-xs font-semibold text-black tracking-tight">Localização</span>
            </div>
            <Row gutter={16}>
              <Col xs={24} sm={12}><InfoItem label="Coordenada Geográfica" value={d.coordenada_geografica} /></Col>
              <Col xs={24} sm={12}><InfoLink label="Mapa do Local" href={mapsUrl} text={mapsUrl ? 'Abrir no Google Maps' : undefined} /></Col>
            </Row>
            {(d.link_street_view || d.thumbnail_street_view) && (
              <Row gutter={16}>
                <Col xs={24} sm={12}><InfoLink label="Street View" href={d.link_street_view} text="Abrir Street View" /></Col>
                {d.thumbnail_street_view && (
                  <Col xs={24} sm={12}>
                    <img src={d.thumbnail_street_view} alt="Street View" className="rounded-md border border-gray-200 max-h-40" />
                  </Col>
                )}
              </Row>
            )}
            {d.tipo_localizacao === 'rural' && (
              <Row gutter={16}>
                <Col xs={24}><InfoItem label="Coordenadas do Transformador" value={d.coordenadas_transformador} /></Col>
              </Row>
            )}
          </div>
        )}
        {materiaisCalc && propostaMaisRecente && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 rounded-full bg-black" />
              <span className="text-xs font-semibold text-black tracking-tight">Materiais (proposta mais recente do Calc{propostaMaisRecente.NumeroProposta ? ` nº ${propostaMaisRecente.NumeroProposta}` : ''})</span>
            </div>
            <div className="mb-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              Esta homologação foi salva sem proposta vinculada (nenhuma proposta válida no Calc na época). Abaixo, só para consulta, os materiais da proposta mais recente do cliente.
            </div>
            <Row gutter={16}>
              <Col xs={12} sm={8}><InfoItem label="Módulos" value={materiaisCalc.modeloModulo ? `${materiaisCalc.qtdModulos ? `${materiaisCalc.qtdModulos}x ` : ''}${materiaisCalc.modeloModulo}` : ''} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Marca dos Inversores" value={materiaisCalc.marcaInversores} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Valor da Proposta" value={formatarBRL(materiaisCalc.valorProposta)} /></Col>
            </Row>
            <div className="mb-2">
              <span className="text-xs text-gray-500 block">Inversores</span>
              {materiaisCalc.inversores.length ? (
                <ul className="text-sm text-gray-800">
                  {materiaisCalc.inversores.map((inv) => <li key={inv.modelo}><b>{inv.quantidade}x</b> {inv.modelo}</li>)}
                </ul>
              ) : <span className="text-sm text-gray-300">-</span>}
            </div>
          </div>
        )}
        {hasMateriais && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 rounded-full bg-black" />
              <span className="text-xs font-semibold text-black tracking-tight">Materiais (proposta{d.numeroProposta ? ` nº ${d.numeroProposta}` : ''})</span>
            </div>
            {propostaDesatualizada && propostaMaisRecente && (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Existe uma proposta mais recente para este cliente
                {propostaMaisRecente.NumeroProposta ? <> (nº <b>{propostaMaisRecente.NumeroProposta}</b></> : ' ('}
                {(propostaMaisRecente as any).Data ? `, ${new Date(String((propostaMaisRecente as any).Data).replace(' ', 'T')).toLocaleDateString('pt-BR')}` : ''})
                {dadosPropostaRecente && (
                  <> — {dadosPropostaRecente.qtdModulos}x {dadosPropostaRecente.modeloModulo}; {dadosPropostaRecente.inversores.map((i) => `${i.quantidade}x ${i.modelo}`).join(' + ')}</>
                )}
                . Esta homologação continua vinculada à proposta nº {d.numeroProposta || '-'}. Se a nova proposta for a que vale,
                abra em Editar e, na etapa Projeto, clique em “Recarregar proposta”{d.procuracao_autentique_doc_id ? ' — atenção: trocar a proposta invalida a procuração já enviada e ela terá de ser reenviada' : ''}.
              </div>
            )}
            <Row gutter={16}>
              <Col xs={12} sm={8}><InfoItem label="Módulos" value={d.modeloModulo ? `${d.numero_modulos ? `${d.numero_modulos}x ` : ''}${d.modeloModulo}` : ''} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Marca dos Inversores" value={d.marcaInversores} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Valor da Proposta" value={formatarBRL(d.valorProposta)} /></Col>
            </Row>
            <div className="mb-2">
              <span className="text-xs text-gray-500 block">Inversores</span>
              {inversoresLista.length ? (
                <ul className="text-sm text-gray-800">
                  {inversoresLista.map((inv) => <li key={inv.modelo}><b>{inv.quantidade}x</b> {inv.modelo}</li>)}
                </ul>
              ) : <span className="text-sm text-gray-300">-</span>}
            </div>
            <Row gutter={16}>
              <Col xs={12} sm={8}><InfoItem label="Qtd. Total de Inversores" value={d.qtdInversores || ''} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Potência Total dos Inversores (kW)" value={d.potenciaTotalInversores || ''} /></Col>
            </Row>
          </div>
        )}
        {hasContratoProjeto && d.homologacao_previa === true && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 rounded-full bg-black" />
              <span className="text-xs font-semibold text-black tracking-tight">Dados para o Contrato</span>
            </div>
            <Row gutter={16}>
              <Col xs={12} sm={8}><InfoItem label="Modalidade de Compensação" value={d.modalidadeCompensacao} /></Col>
              {/* Vazios no contrato → mostra o que a própria homologação já registrou. */}
              <Col xs={12} sm={8}><InfoItem label="Local de Instalação (Urbano/Rural)" value={d.localInstalacao || capitalizar(d.tipo_localizacao)} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Tipo de Estrutura (contrato)" value={d.tipoEstrutura || estruturaInstalacao} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12} sm={12}><InfoItem label="Contrato com Seguro" value={d.contratoComSeguro ? 'Sim' : 'Não'} /></Col>
            </Row>
          </div>
        )}
      </div>

      {/* Padrão Atual */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">Padrão Atual</h2>
        <Row gutter={16}>
          <Col xs={12} sm={6}><InfoItem label="Tipo de Padrão" value={d.tipo_padrao} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Contas Mesmo Titular" value={d.todas_contas_mesmo_titular} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Corrente do Padrão" value={d.corrente_padrao} /></Col>
          <Col xs={12} sm={6}><InfoItem label="Fases do Disjuntor Atual" value={FASES_LABELS[d.fases_disjuntor] || d.fases_disjuntor} /></Col>
        </Row>
        {d.tipo_padrao === 'Coletivo' && (
          <Row gutter={16}>
            <Col xs={12} sm={6}><InfoItem label="Tem Disjuntor Geral?" value={d.tem_disjuntor_geral} /></Col>
            {d.tem_disjuntor_geral === 'Sim' && (
              <Col xs={12} sm={6}><InfoItem label="Corrente Disjuntor Geral" value={d.corrente_disjuntor_geral} /></Col>
            )}
          </Row>
        )}
        {d.tem_alteracao_carga === 'Sim' && (
          <Row gutter={16}>
            <Col xs={12} sm={8}><InfoItem label="Alteração de Carga" value="Sim" /></Col>
            <Col xs={12} sm={8}><InfoItem label="Tipo de Alteração" value={d.tipo_alteracao_carga} /></Col>
            <Col xs={24}><InfoItem label="Descrição da Alteração" value={d.descricao_alteracao} /></Col>
            {d.coord_padrao_novo && <Col xs={24}><InfoItem label="Coordenadas Novo Padrão (UTM)" value={d.coord_padrao_novo} /></Col>}
          </Row>
        )}
        {d.tipo_padrao === 'Coletivo' && d.alteracao_coletivo === 'Sim' && (
          <Row gutter={16}>
            <Col xs={24}><InfoItem label="Alteração Padrão Coletivo" value={d.descricao_alteracao_coletivo} /></Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col xs={24}><InfoItem label="Descrição do Padrão" value={d.descricao_padrao} /></Col>
        </Row>
      </div>

      {/* Serviços Adicionais */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">Serviços Adicionais</h2>
        <Row gutter={16}>
          <Col xs={24}><InfoItem label="Serviço Adicional" value={d.servico_adicional} /></Col>
        </Row>
        {d.descricao_servico_adicional && <Row gutter={16}><Col xs={24}><InfoItem label="Descrição do Serviço de Padrão" value={d.descricao_servico_adicional} /></Col></Row>}
        {d.favor_contra_rede && (
          <Row gutter={16}>
            <Col xs={8}><InfoItem label="Padrão em Relação à Rede" value={d.favor_contra_rede} /></Col>
            <Col xs={8}><InfoItem label="Serviço de Alvenaria" value={d.servico_alvenaria} /></Col>
            <Col xs={8}><InfoItem label="Valor do Padrão" value={formatarBRL(d.valor_padrao) || d.valor_padrao} /></Col>
          </Row>
        )}
        {d.aditivos && <Row gutter={16}><Col xs={24}><InfoItem label="Serviço Adicional (descrição)" value={d.aditivos} /></Col></Row>}
        {hasContratoServicos && d.homologacao_previa === true && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 rounded-full bg-black" />
              <span className="text-xs font-semibold text-black tracking-tight">Dados para o Contrato</span>
            </div>
            <Row gutter={16}>
              <Col xs={12} sm={8}><InfoItem label="Situação do Padrão" value={d.situacaoPadrao} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Quantidade de Padrões" value={d.quantidadePadroes} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Tipo de Conexão do Padrão" value={d.tipoConexaoPadrao || FASES_LABELS[d.fases_disjuntor]} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12} sm={8}><InfoItem label="Corrente do Disjuntor (contrato)" value={d.correnteDisjuntor || d.corrente_padrao} /></Col>
              <Col xs={12} sm={8}><InfoItem label="A Favor ou Contra a Rede" value={d.favorOuContraRede} /></Col>
              <Col xs={12} sm={8}><InfoItem label="Serviço de Alvenaria (contrato)" value={d.servicoAlvenariaWiz} /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}><InfoItem label="Valor do Padrão" value={formatarBRL(d.valorPadrao)} /></Col>
            </Row>
            {d.situacaoPadrao === 'Alteração de carga' && d.coordPadraoNovo && (
              <Row gutter={16}>
                <Col xs={24}><InfoItem label="Coordenadas do novo padrão (contrato)" value={d.coordPadraoNovo} /></Col>
              </Row>
            )}
            {d.aditivos && (
              <Row gutter={16}>
                <Col xs={24}><InfoItem label="Aditivos do Contrato" value={d.aditivos} /></Col>
              </Row>
            )}
          </div>
        )}
      </div>

      {/* Observações */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-bold text-blue-800 border-b border-blue-100 pb-2 mb-4">Observações</h2>
        <InfoItem label="Observações sobre Fotos" value={d.observacoes_fotos} />
        <InfoItem label="Observações Gerais" value={d.observacoes_finais} />
      </div>

      {/* Arquivos / Fotos */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between border-b border-blue-100 pb-2 mb-4">
          <h2 className="text-lg font-bold text-blue-800">
            Arquivos e Fotos
            <Tag className="ml-2">{arquivos.length} total</Tag>
          </h2>
          <div className="flex gap-2">
            <Button icon={<PlusOutlined />} size="small" onClick={() => setUploadCategoria('_novo')}>
              Adicionar Arquivo
            </Button>
            {arquivos.length > 0 && (
              <Button icon={<DownloadOutlined />} size="small" onClick={() => {
                for (const arq of arquivos) {
                  const url = obterUrlDownloadHomologacao(arq.id);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = arq.nome_original;
                  a.target = '_blank';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              }}>
                Baixar todos
              </Button>
            )}
          </div>
        </div>

        {/* Upload inline */}
        {uploadCategoria && (
          <div className="mb-4 p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
            <div className="flex items-center gap-3 mb-3">
              <select value={uploadCategoria} onChange={(e) => setUploadCategoria(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
                <option value="_novo">Selecione a categoria</option>
                {Object.entries(CATEGORIA_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                <option value="outros">Outros</option>
              </select>
              <label className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-200 text-gray-400' : 'bg-black text-white hover:bg-gray-800'}`}>
                <CloudUploadOutlined />
                {uploading ? 'Enviando...' : 'Escolher Arquivo'}
                <input type="file" className="hidden" multiple accept="image/*,.pdf" disabled={uploading || uploadCategoria === '_novo'}
                  onChange={(e) => {
                    if (!e.target.files) return;
                    for (const f of Array.from(e.target.files)) handleUpload(uploadCategoria, f);
                    e.target.value = '';
                  }} />
              </label>
              <button onClick={() => setUploadCategoria('')} className="text-xs text-gray-400 hover:text-gray-600">Cancelar</button>
            </div>
            {uploadCategoria === '_novo' && <p className="text-xs text-gray-400">Selecione uma categoria antes de enviar.</p>}
          </div>
        )}

        {arquivos.length === 0 && !uploadCategoria ? (
          <Empty description="Nenhum arquivo anexado" />
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, arqs]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-gray-600">
                    {CATEGORIA_LABELS[cat] || cat}
                    <Tag className="ml-2">{arqs.length}</Tag>
                  </h3>
                  {arqs.length > 1 && (
                    <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => {
                      for (const arq of arqs) {
                        const url = obterUrlDownloadHomologacao(arq.id);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = arq.nome_original;
                        a.target = '_blank';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }
                    }}>
                      Baixar
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-3">
                  {arqs.map((arq) => <ArquivoThumb key={arq.id} arq={arq} onDelete={handleDelete} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Input oculto pra anexar procuração já assinada */}
      <input
        ref={anexarAssinadaInputRef}
        type="file"
        accept="application/pdf,image/*"
        style={{ display: 'none' }}
        onChange={handleAnexarAssinadaFile}
      />

      {/* Modal Procuração */}
      <Modal
        title={null}
        open={procModalOpen}
        onCancel={() => setProcModalOpen(false)}
        footer={null}
        width="95vw"
        style={{ maxWidth: 720 }}
        destroyOnClose
      >
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button onClick={() => setProcTab('contratos')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${procTab === 'contratos' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            Gerar Procuração
          </button>
          <button onClick={() => { setProcTab('autentique'); if (!autStatus) loadAutentiqueStatus(); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${procTab === 'autentique' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            Assinatura Digital
            {autStatus?.enviada && (
              <Tag color={autStatus.status === 'Assinado' ? 'green' : autStatus.status === 'Recusado' ? 'red' : 'gold'} className="text-[10px] ml-1">
                {autStatus.status}
              </Tag>
            )}
          </button>
        </div>

        {/* Tab: Contratos */}
        {procTab === 'contratos' && (
          <>
          {reenviando && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center justify-between">
              <span>Modo reenvio: escolha o contrato (ou preencha manualmente). A procuração atual será arquivada.</span>
              <button onClick={() => setReenviando(false)} className="text-amber-900 hover:underline ml-3">Cancelar</button>
            </div>
          )}
          {procBuscando ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <Spin size="large" />
              <span className="text-gray-500 text-sm">Buscando contratos do cliente...</span>
            </div>
          ) : modoManual ? (
            <FormularioManualProcuracao
              dados={dadosManuais}
              onChange={setDadosManuais}
              signerForm={signerForm}
              onSignerChange={setSignerForm}
              onGerar={() => handleGerarProcuracao()}
              onEnviar={handleEnviarAutentique}
              onAnexarAssinada={() => triggerAnexarAssinada({ manual: true })}
              onVoltar={contratos.length > 0 ? () => setModoManual(false) : undefined}
              gerando={procGerando === 'manual'}
              loading={autLoading}
              anexando={anexandoAssinada}
              somentePreenchimento={!!data?.somentePreenchimento}
            />
          ) : contratos.length === 0 ? (
            <div className="py-6 text-center">
              <Empty description={<span className="text-gray-500 text-sm">Nenhum contrato encontrado para este cliente</span>} className="mb-4" />
              <button onClick={() => setModoManual(true)} className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-neutral-800 transition-colors">
                Preencher dados manualmente
              </button>
            </div>
          ) : (
            <div>
              {clienteInfo.nome && (
                <p className="text-sm text-gray-500 mb-4">
                  Cliente: <strong>{clienteInfo.nome}</strong>
                  {clienteInfo.cpf && <span className="ml-1 text-gray-400">({clienteInfo.cpf})</span>}
                  <span className="ml-2"><Tag>{contratos.length} contrato(s)</Tag></span>
                </p>
              )}
              <div className="space-y-3">
                {contratos.map((c) => (
                  <div key={c.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-blue-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm text-gray-800">{c.nomeResponsavel || '-'}</span>
                      <Tag color={c.statusContrato === 'aberto' ? 'blue' : c.statusContrato === 'assinado' ? 'green' : 'default'} className="text-xs">{c.statusContrato}</Tag>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                      <div><span className="font-medium">CPF:</span> {c.cpfResponsavel || '-'}</div>
                      <div><span className="font-medium">Tipo:</span> {c.tipoCliente || '-'}</div>
                      <div><span className="font-medium">Estado Civil:</span> {c.estadoCivil || '-'}</div>
                      <div><span className="font-medium">Profissão:</span> {c.profissao || '-'}</div>
                      <div className="sm:col-span-2">
                        <span className="font-medium">Endereço:</span>{' '}
                        {[c.logradouroInstalacao, c.numeroInstalacao, c.bairroInstalacao].filter(Boolean).join(', ')}
                        {c.cidadeInstalacao && ` - ${c.cidadeInstalacao}`}{c.ufInstalacao && `/${c.ufInstalacao}`}
                      </div>
                      <div><span className="font-medium">Potência:</span> {c.potenciaUsada} kWp</div>
                      <div><span className="font-medium">Criado:</span> {new Date(c.createdAt).toLocaleDateString('pt-BR')}</div>
                    </div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <span className="text-xs font-medium text-gray-600">Procurador:</span>
                      <Tag color={c.procuradorSelecionado === 'Marcos' ? 'blue' : 'orange'} className="text-xs">{c.procuradorSelecionado}</Tag>
                      <span className="text-[10px] text-gray-400">
                        ({c.tipoCliente?.includes('Física') || c.tipoCliente?.toUpperCase().includes('PF') ? 'PF' : 'PJ'} + {c.potenciaUsada} kWp{c.potenciaUsada <= 60 ? ' ≤ 60' : ' > 60'})
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="primary" icon={<DownloadOutlined />} loading={procGerando === c.id} onClick={() => handleGerarProcuracao(c.id)} className="flex-1 sm:flex-none">PDF</Button>
                      {(!autStatus?.enviada || reenviando) && !data?.somentePreenchimento && (
                        <Button icon={<SendOutlined />} onClick={() => { setSelectedContratoId(c.id); setSignerForm(prev => ({ ...prev, name: c.nomeResponsavel || '' })); setProcTab('autentique'); }} className="flex-1 sm:flex-none">{reenviando ? 'Reenviar para este' : 'Assinar'}</Button>
                      )}
                      {(!autStatus?.enviada || reenviando) && !data?.somentePreenchimento && (
                        <Button
                          icon={<CloudUploadOutlined />}
                          loading={anexandoAssinada}
                          onClick={() => {
                            const nome = signerForm.name || c.nomeResponsavel || '';
                            setSelectedContratoId(c.id);
                            setSignerForm(prev => ({ ...prev, name: nome }));
                            triggerAnexarAssinada({ contratoId: c.id, manual: false, signerNameOverride: nome });
                          }}
                          className="flex-1 sm:flex-none"
                        >
                          Já tenho assinada
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 text-center">
                <button onClick={() => setModoManual(true)} className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2">
                  Usar dados manuais em vez do contrato
                </button>
              </div>
            </div>
          )}
          </>
        )}

        {/* Tab: Assinatura Digital */}
        {procTab === 'autentique' && (
          autStatus?.enviada && !reenviando ? (
            <div>
              {/* Status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">Status:</span>
                  <Tag color={(autSyncData?.status || autStatus.status) === 'Assinado' ? 'green' : (autSyncData?.status || autStatus.status) === 'Recusado' ? 'red' : 'gold'} className="text-sm px-3 py-0.5">
                    {autSyncData?.status || autStatus.status || 'Aguardando'}
                  </Tag>
                </div>
                <div className="flex gap-2">
                  <Button icon={<SyncOutlined spin={autSyncing} />} size="small" onClick={handleSyncAutentique} loading={autSyncing}>Atualizar</Button>
                  <Button icon={<SendOutlined />} size="small" onClick={() => {
                    const signerExistente = autSyncData?.signers?.[0];
                    setReenviando(true);
                    setProcTab('contratos');
                    setSignerForm(prev => ({
                      ...prev,
                      name: signerExistente?.name || prev.name,
                      email: signerExistente?.email || prev.email,
                    }));
                  }}>Reenviar</Button>
                  {(autSyncData?.status || autStatus.status) !== 'Assinado' && (
                    <Button icon={<DeleteOutlined />} size="small" danger onClick={handleCancelarAutentique} loading={autLoading}>Cancelar</Button>
                  )}
                </div>
              </div>

              {autStatus.createdAt && (
                <p className="text-xs text-gray-400 mb-4">Enviado em: {new Date(autStatus.createdAt).toLocaleString('pt-BR')}</p>
              )}

              {/* Signatários */}
              {autSyncData?.signers && autSyncData.signers.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 mb-2">Signatário:</p>
                  {autSyncData.signers.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{s.name}</p>
                        <p className="text-xs text-gray-500">{s.email || 'Via WhatsApp/SMS'}</p>
                        {s.signedAt && <p className="text-xs text-green-600">Assinado em: {new Date(s.signedAt).toLocaleString('pt-BR')}</p>}
                        {s.rejectedAt && <p className="text-xs text-red-600">Recusado em: {new Date(s.rejectedAt).toLocaleString('pt-BR')}{s.rejectedReason && ` - ${s.rejectedReason}`}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {s.link && s.status !== 'Assinado' && s.status !== 'Recusado' && (
                          <Button size="small" onClick={() => { navigator.clipboard.writeText(s.link!); toast.success('Link copiado!'); }}>Copiar Link</Button>
                        )}
                        <Tag color={s.status === 'Assinado' ? 'green' : s.status === 'Recusado' ? 'red' : s.status === 'Visualizado' ? 'blue' : 'gold'}>{s.status}</Tag>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Link do documento assinado */}
              {autSyncData?.signedUrl && (autSyncData.status === 'Assinado') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <Button type="primary" icon={<DownloadOutlined />} onClick={() => window.open(autSyncData.signedUrl, '_blank')}>
                    Baixar Procuração Assinada
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div>
              {!selectedContratoId && contratos.length > 0 ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <FileTextOutlined className="text-2xl text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Nenhum contrato selecionado</p>
                  <p className="text-xs text-gray-400">Vá em "Gerar Procuração" e clique em <strong>Assinar</strong> no contrato desejado.</p>
                </div>
              ) : selectedContratoId && (
                <div>
                  {/* Card do signatário */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 text-xs font-bold">1</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">Signatário (Titular)</p>
                        <p className="text-[10px] text-gray-400">Sem autenticação adicional</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Nome completo *</label>
                        <input type="text" value={signerForm.name} onChange={e => setSignerForm(p => ({ ...p, name: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all" placeholder="Nome do titular" />
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Enviar via</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: 'email', label: 'E-mail', icon: '✉' },
                            { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
                            { value: 'sms', label: 'SMS', icon: '📱' },
                          ].map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => setSignerForm(p => ({ ...p, deliveryMethod: opt.value }))}
                              className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                                signerForm.deliveryMethod === opt.value
                                  ? 'bg-black text-white border-black'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                              }`}>
                              <span>{opt.icon}</span> {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {signerForm.deliveryMethod === 'email' ? (
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">E-mail *</label>
                          <input type="email" value={signerForm.email} onChange={e => setSignerForm(p => ({ ...p, email: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all" placeholder="email@exemplo.com" />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Telefone *</label>
                          <input type="tel" value={signerForm.phone} onChange={e => setSignerForm(p => ({ ...p, phone: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all" placeholder="(31) 99999-9999" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mensagem */}
                  <div className="mb-4">
                    <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Mensagem personalizada</label>
                    <textarea value={signerForm.message} onChange={e => setSignerForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all resize-none" rows={2} />
                  </div>

                  {reenviando && (
                    <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                      Reenvio: a procuração atual será arquivada e uma nova será gerada e enviada.
                    </div>
                  )}

                  {/* Ações */}
                  <div className="flex gap-3">
                    <button onClick={() => { if (reenviando) setReenviando(false); else setProcTab('contratos'); }}
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                      {reenviando ? 'Cancelar reenvio' : 'Voltar'}
                    </button>
                    <button onClick={() => handleGerarProcuracao(selectedContratoId || undefined)}
                      disabled={!!procGerando}
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                      {procGerando ? (
                        <><SyncOutlined spin /> Gerando...</>
                      ) : (
                        <><DownloadOutlined /> Baixar PDF</>
                      )}
                    </button>
                    {data?.somentePreenchimento ? (
                      <div className="flex-[2] py-2.5 px-3 rounded-lg text-xs bg-amber-50 border border-amber-200 text-amber-900 flex items-center">
                        Cliente de representante: o envio para assinatura é feito pela administração.
                      </div>
                    ) : (
                    <button onClick={handleEnviarAutentique} disabled={autLoading}
                      className="flex-[2] py-2.5 rounded-lg text-sm font-medium bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                      {autLoading ? (
                        <><SyncOutlined spin /> Enviando...</>
                      ) : (
                        <><SendOutlined /> {reenviando ? 'Reenviar para Assinatura' : 'Enviar para Assinatura'}</>
                      )}
                    </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </Modal>
    </div>
  );
}
